import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

// Types
export type SupplierCategory =
  | 'UTILITIES'
  | 'RENT'
  | 'INSURANCE'
  | 'MAINTENANCE'
  | 'IT_SERVICES'
  | 'PROFESSIONAL'
  | 'OFFICE_SUPPLIES'
  | 'EQUIPMENT'
  | 'MARKETING'
  | 'TRANSPORT'
  | 'BANKING'
  | 'GOVERNMENT'
  | 'OTHER';

export interface Supplier {
  id: string;
  name: string;
  shortName?: string;
  ice?: string;
  ifiscal?: string;
  rc?: string;
  patente?: string;
  cnss?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  website?: string;
  contactPerson?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country: string;
  bankName?: string;
  bankRib?: string;
  bankIban?: string;
  bankSwift?: string;
  paymentTermsDays: number;
  defaultTvaRate: number;
  category: SupplierCategory;
  isActive: boolean;
  archivedAt?: string | null;
  notes?: string;
  contractUrl?: string | null;
  contractFileName?: string | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  contractNotes?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    expenses: number;
  };
}

export interface CreateSupplierDto {
  name: string;
  shortName?: string;
  ice?: string;
  ifiscal?: string;
  rc?: string;
  patente?: string;
  cnss?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  website?: string;
  contactPerson?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  bankName?: string;
  bankRib?: string;
  bankIban?: string;
  bankSwift?: string;
  paymentTermsDays?: number;
  defaultTvaRate?: number;
  category?: SupplierCategory;
  notes?: string;
}

export interface UpdateSupplierDto extends Partial<CreateSupplierDto> {
  isActive?: boolean;
}

export interface SupplierFilters {
  search?: string;
  category?: SupplierCategory;
  isActive?: boolean;
  archived?: boolean;
}

export interface SupplierStatistics {
  total: number;
  active: number;
  inactive: number;
  archived?: number;
  byCategory: Record<string, number>;
}

// API Functions
export const getSuppliers = async (filters?: SupplierFilters): Promise<Supplier[]> => {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.category) params.append('category', filters.category);
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  if (filters?.archived !== undefined) params.append('archived', filters.archived.toString());

  const query = params.toString() ? `?${params.toString()}` : '';
  return apiGet<Supplier[]>(`/billing/suppliers${query}`);
};

export const getSupplier = async (id: string): Promise<Supplier> => {
  return apiGet<Supplier>(`/billing/suppliers/${id}`);
};

export const createSupplier = async (data: CreateSupplierDto): Promise<Supplier> => {
  return apiPost<Supplier>('/billing/suppliers', data);
};

export const updateSupplier = async (id: string, data: UpdateSupplierDto): Promise<Supplier> => {
  return apiPatch<Supplier>(`/billing/suppliers/${id}`, data);
};

export const deleteSupplier = async (id: string): Promise<void> => {
  return apiDelete<void>(`/billing/suppliers/${id}`);
};

export const archiveSupplier = async (id: string): Promise<Supplier> => {
  return apiPost<Supplier>(`/billing/suppliers/${id}/archive`, {});
};

export const unarchiveSupplier = async (id: string): Promise<Supplier> => {
  return apiPost<Supplier>(`/billing/suppliers/${id}/unarchive`, {});
};

export const uploadSupplierContract = async (
  id: string,
  file: File,
  meta?: { contractStartDate?: string; contractEndDate?: string; contractNotes?: string },
): Promise<Supplier> => {
  const token = localStorage.getItem('nexedu_access_token');
  const formData = new FormData();
  formData.append('file', file);
  if (meta?.contractStartDate) formData.append('contractStartDate', meta.contractStartDate);
  if (meta?.contractEndDate) formData.append('contractEndDate', meta.contractEndDate);
  if (meta?.contractNotes) formData.append('contractNotes', meta.contractNotes);

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/billing/suppliers/${id}/contract`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Erreur lors de l\'upload du contrat' }));
    throw new Error(err.message || 'Erreur lors de l\'upload du contrat');
  }
  return response.json();
};

export const removeSupplierContract = async (id: string): Promise<Supplier> => {
  return apiDelete<Supplier>(`/billing/suppliers/${id}/contract`);
};

export const getSupplierStatistics = async (): Promise<SupplierStatistics> => {
  return apiGet<SupplierStatistics>('/billing/suppliers/statistics');
};

export const getSupplierCategories = async (): Promise<Record<SupplierCategory, string>> => {
  return apiGet<Record<SupplierCategory, string>>('/billing/suppliers/categories');
};

// Helper functions
export const getCategoryLabel = (category: SupplierCategory): string => {
  const labels: Record<SupplierCategory, string> = {
    UTILITIES: 'Utilities (Eau, Électricité, Télécom)',
    RENT: 'Loyers et charges locatives',
    INSURANCE: 'Assurances',
    MAINTENANCE: 'Entretien et réparations',
    IT_SERVICES: 'Services informatiques',
    PROFESSIONAL: 'Honoraires professionnels',
    OFFICE_SUPPLIES: 'Fournitures de bureau',
    EQUIPMENT: 'Équipements',
    MARKETING: 'Publicité et communication',
    TRANSPORT: 'Transport et déplacements',
    BANKING: 'Services bancaires',
    GOVERNMENT: 'Impôts et taxes',
    OTHER: 'Autres',
  };
  return labels[category] || category;
};

export const getCategoryColor = (category: SupplierCategory): string => {
  const colors: Record<SupplierCategory, string> = {
    UTILITIES: 'bg-blue-100 text-blue-800',
    RENT: 'bg-purple-100 text-purple-800',
    INSURANCE: 'bg-green-100 text-green-800',
    MAINTENANCE: 'bg-orange-100 text-orange-800',
    IT_SERVICES: 'bg-cyan-100 text-cyan-800',
    PROFESSIONAL: 'bg-indigo-100 text-indigo-800',
    OFFICE_SUPPLIES: 'bg-gray-100 text-gray-800',
    EQUIPMENT: 'bg-yellow-100 text-yellow-800',
    MARKETING: 'bg-pink-100 text-pink-800',
    TRANSPORT: 'bg-teal-100 text-teal-800',
    BANKING: 'bg-emerald-100 text-emerald-800',
    GOVERNMENT: 'bg-red-100 text-red-800',
    OTHER: 'bg-slate-100 text-slate-800',
  };
  return colors[category] || 'bg-gray-100 text-gray-800';
};

// Format supplier display name
export const formatSupplierName = (supplier: Supplier | null | undefined): string => {
  if (!supplier) return 'Fournisseur inconnu';
  return supplier.shortName ? `${supplier.shortName} (${supplier.name})` : supplier.name;
};

// Validate ICE (15 digits)
export const validateIce = (ice: string): boolean => {
  return /^\d{15}$/.test(ice.replace(/\s/g, ''));
};

// Validate RIB (24 digits)
export const validateRib = (rib: string): boolean => {
  return /^\d{24}$/.test(rib.replace(/\s/g, ''));
};
